import { Button, Card, Group, Modal, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { unwrap } from '../api/client';
import { useOrg } from '../api/org';
import { createProject, listProjects } from '../api/gen';

export function ProjectsPage() {
  const queries = useQueryClient();
  const headers = useOrg();
  const [opened, { open, close }] = useDisclosure();
  const [slug, setSlug] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => unwrap(listProjects({ headers, query: { limit: 100 } })),
  });

  const create = useMutation({
    mutationFn: (s: string) => unwrap(createProject({ headers, body: { slug: s } })),
    onSuccess: () => {
      queries.invalidateQueries({ queryKey: ['projects'] });
      setSlug('');
      close();
    },
  });

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Projects</Title>
        <Button onClick={open}>New project</Button>
      </Group>

      <Text c="dimmed" size="sm">
        A project holds agents, knowledge stores and environments. Its slug is
        what a folder&rsquo;s <code>mindvm.jsonc</code> names, and the first
        <code> mindvm dev</code> creates it.
      </Text>

      <Card withBorder p={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Slug</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Description</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr>
                <Table.Td colSpan={3}>Loading…</Table.Td>
              </Table.Tr>
            )}
            {data?.data.map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td>
                  <code>{p.slug}</code>
                </Table.Td>
                <Table.Td>{p.name}</Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {p.description}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
            {data?.data.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={3}>
                  <Text c="dimmed">
                    Nothing yet. Run <code>mindvm dev</code> in an agent folder,
                    or make one here.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal opened={opened} onClose={close} title="New project">
        <Stack>
          <TextInput
            label="Slug"
            description="Lower-case letters, digits and single hyphens. This is the handle the CLI sends."
            value={slug}
            onChange={(e) => setSlug(e.currentTarget.value)}
          />
          {create.isError && (
            <Text c="red" size="sm">
              {(create.error as Error).message}
            </Text>
          )}
          <Button disabled={!slug} loading={create.isPending} onClick={() => create.mutate(slug)}>
            Create
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
